import { invariantResponse } from "@epic-web/invariant"
import {
	type LoaderFunctionArgs,
	json,
	type MetaFunction,
} from "@remix-run/node"
import { useLoaderData } from "@remix-run/react"
import { serverOnly$ } from "vite-env-only/macros"
import ProductDetail from "#app/components/admin/products/detail"
import { getCartSessionId } from "#app/utils/cart.server"
import { APP_NAME } from "#app/utils/constants.js"
import { prisma } from "#app/utils/db.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import routes from "#routes/index.js"
import { getPublicProduct } from "../admin+/products+/product.server"

export async function loader({ request, params }: LoaderFunctionArgs) {
	const { handle } = params
	const { sessionId } = await getCartSessionId(request)

	// Get the product.
	const product = await getPublicProduct(handle)

	invariantResponse(product, "Not found", {
		status: 404,
	})

	// Check the quantity of this product that there already is the user's cart.
	let amountInCart = 0

	const cart = await prisma.cart.findUnique({
		where: {
			sessionId,
		},
		select: {
			id: true,
		},
	})

	if (cart && cart.id) {
		const item = await prisma.cartItem.findFirst({
			where: {
				cartId: cart.id,
				productId: product.id,
			},
			select: {
				quantity: true,
			},
		})

		if (item && item.quantity > 0) amountInCart = item?.quantity
	}

	return json({
		amountInCart,
		product,
	})
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return generateMeta({
		title: `${data?.product.name} - Greta's Patchwork`,
		description: `Descubre la belleza atemporal del patchwork con ${data?.product.name} de ${APP_NAME}. Cada pieza de patchwork está hecha a mano con cuidado y atención al detalle, lo que garantiza una calidad y durabilidad excepcionales.`,
		canonical: data
			? routes.public.product.route(data.product.handle)
			: undefined,
	})
}

export const handle = serverOnly$({
	getSitemapEntries: async () => {
		const products = await prisma.product.findMany({
			select: {
				id: true,
				handle: true,
			},
		})

		return products.map(product => ({
			route: routes.public.product.route(product.handle),
			lastModified: new Date(),
			changeFrequency: "weekly",
			priority: 0.8,
		}))
	},
})

export default function AdminPreviewProduct() {
	const { product, amountInCart } = useLoaderData<typeof loader>()

	return (
		<div className="container my-8">
			<ProductDetail showActions {...product} amountInCart={amountInCart} />
		</div>
	)
}
